// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useId,
  useLayoutEffect,
} from 'react';
import React from 'react';
import { createPortal } from 'react-dom';
import { DropdownChevronIcon } from './TablePaginationIcon';
import { Checkbox } from './Checkbox';
import type { DropdownOption } from './Dropdown';
import './Dropdown.css';
import './MultiSelect.css';

export interface MultiSelectProps {
  label?: string;
  placeholder?: string;
  options: DropdownOption[];
  /** The currently-selected option values. */
  selectedValues: string[];
  onChange: (values: string[]) => void;
  required?: boolean;
  disabled?: boolean;
  fluid?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * Shared multi-select: a single tab-stop trigger + a portalled checkbox list
 * with the same keyboard model as {@link Dropdown} — Arrow keys move the
 * highlight, Space/Enter toggle the highlighted option (the menu stays open),
 * Space/Arrows/Enter open a closed menu, Tab/Escape close, and type-ahead jumps
 * to the first option matching typed letters (repeat a letter to cycle).
 */
export function MultiSelect({
  label,
  placeholder = 'Select...',
  options,
  selectedValues,
  onChange,
  required,
  disabled,
  fluid = false,
  className = '',
  id: idProp,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: MultiSelectProps) {
  const autoId = useId();
  const id = idProp ?? autoId;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const typeahead = useRef<{ buffer: string; timer: ReturnType<typeof setTimeout> | undefined }>({
    buffer: '',
    timer: undefined,
  });
  const highlightedIndexRef = useRef(-1);

  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const selectedSet = new Set(selectedValues);
  const selectedOptions = options.filter((o) => selectedSet.has(o.value));
  const isFilled = selectedOptions.length > 0;
  const displayText = isFilled ? selectedOptions.map((o) => o.text).join(', ') : placeholder;

  // Close on click outside.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Position the portal menu under the trigger, flipping up when tight.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;
    const updatePosition = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight ?? 240;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < menuHeight + gap && rect.top > spaceBelow;
      setMenuStyle({
        position: 'fixed',
        top: flipUp ? undefined : rect.bottom + gap,
        bottom: flipUp ? window.innerHeight - rect.top + gap : undefined,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex;
  }, [highlightedIndex]);

  useEffect(() => {
    if (highlightedIndex >= 0 && menuRef.current) {
      const items = menuRef.current.querySelectorAll('[role="option"]');
      const item = items[highlightedIndex];
      if (item && typeof item.scrollIntoView === 'function') {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(-1);
  }, [disabled]);

  const toggle = useCallback(
    (optionValue: string) => {
      if (selectedSet.has(optionValue)) {
        onChange(selectedValues.filter((v) => v !== optionValue));
      } else {
        onChange([...selectedValues, optionValue]);
      }
    },
    // selectedSet derives from selectedValues; depend on the primitive array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, selectedValues],
  );

  const typeAhead = useCallback(
    (char: string) => {
      if (options.length === 0) return;
      clearTimeout(typeahead.current.timer);
      const buffer = (typeahead.current.buffer + char).toLowerCase();
      typeahead.current.buffer = buffer;
      typeahead.current.timer = setTimeout(() => {
        typeahead.current.buffer = '';
      }, 500);
      const sameChar = buffer.length > 1 && [...buffer].every((c) => c === buffer[0]);
      const needle = sameChar ? buffer[0] : buffer;
      const from = sameChar ? highlightedIndexRef.current + 1 : 0;
      for (let i = 0; i < options.length; i += 1) {
        const idx = (from + i) % options.length;
        if (options[idx].text.toLowerCase().startsWith(needle)) {
          setHighlightedIndex(idx);
          return;
        }
      }
    },
    [options],
  );

  const handleTriggerClick = useCallback(() => {
    if (disabled) return;
    setIsOpen((v) => !v);
    setHighlightedIndex(-1);
  }, [disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      switch (e.key) {
        case 'Escape':
          if (isOpen) {
            e.preventDefault();
            setIsOpen(false);
            triggerRef.current?.focus();
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) openMenu();
          else setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) openMenu();
          else setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (isOpen && highlightedIndexRef.current >= 0) {
            // Multi-select: toggle and keep the menu open.
            toggle(options[highlightedIndexRef.current].value);
          } else if (!isOpen) {
            openMenu();
          }
          break;
        case 'Tab':
          if (isOpen) setIsOpen(false);
          break;
        default:
          if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            e.preventDefault();
            if (!isOpen) openMenu();
            typeAhead(e.key);
          }
      }
    },
    [disabled, isOpen, options, openMenu, toggle, typeAhead],
  );

  const wrapperClasses = [
    'fin-dropdown',
    disabled && 'fin-dropdown--disabled',
    isFilled && 'fin-dropdown--filled',
    isOpen && 'fin-dropdown--open',
    fluid && 'fin-dropdown--fluid',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClasses} ref={containerRef} aria-label={ariaLabel} data-testid={dataTestId}>
      {label && (
        <label className="fin-dropdown__label" id={`${id}-label`}>
          {label}
          {required && <span className="fin-dropdown__required">*</span>}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        className="fin-dropdown__trigger"
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-activedescendant={
          isOpen && highlightedIndex >= 0 ? `${id}-option-${highlightedIndex}` : undefined
        }
        id={id}
      >
        <span className="fin-dropdown__trigger-inner">
          <span
            className={`fin-dropdown__trigger-text${!isFilled ? ' fin-dropdown__trigger-text--placeholder' : ''}`}
          >
            {displayText}
          </span>
        </span>
        <span className="fin-dropdown__chevron" aria-hidden="true">
          <DropdownChevronIcon />
        </span>
      </button>

      {isOpen &&
        createPortal(
          <ul
            ref={menuRef}
            className="fin-dropdown__menu"
            role="listbox"
            aria-multiselectable="true"
            aria-labelledby={label ? `${id}-label` : undefined}
            style={menuStyle}
          >
            {options.length === 0 ? (
              <li className="fin-dropdown__no-results">No results found</li>
            ) : (
              options.map((opt, index) => {
                const isSelected = selectedSet.has(opt.value);
                return (
                  <li
                    key={opt.value}
                    id={`${id}-option-${index}`}
                    className={[
                      'fin-dropdown__option',
                      index === highlightedIndex && 'fin-dropdown__option--active',
                      isSelected && 'fin-dropdown__option--selected',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggle(opt.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <span className="fin-multiselect__option-inner">
                      <Checkbox checked={isSelected} readOnly tabIndex={-1} className="fin-multiselect__checkbox" />
                      <span>{opt.text}</span>
                    </span>
                  </li>
                );
              })
            )}
          </ul>,
          document.body,
        )}
    </div>
  );
}
